<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ExpertProfileStatus, ListingType } from '@shopexperts/legacy-api';
import { useExpertManagerStats } from '@/app/composables/expert-manager/useExpertManagerStats';
import ExpertStatsSegmentedBar, { type ExpertStatsSegment } from './ExpertStatsSegmentedBar.vue';
import ExpertStatsTypeCard from './ExpertStatsTypeCard.vue';
import { expertTypeAppearance } from './expertTypeAppearance';

const props = defineProps<{
  activeStatus?: string;
  activeExpertType?: string;
}>();

const emit = defineEmits<{
  applyFilters: [filters: { status: ExpertProfileStatus; expertType?: ListingType | '' }];
}>();

const { stats, isLoading } = useExpertManagerStats();
const hoveredOverviewStatus = ref<ExpertProfileStatus | null>(null);

const statusLegend = [
  { key: 'published',   label: 'Published',   dotClass: 'bg-emerald-500', textClass: 'text-emerald-700' },
  { key: 'application', label: 'Application',  dotClass: 'bg-amber-400',   textClass: 'text-amber-700' },
  { key: 'draft',       label: 'Draft',        dotClass: 'bg-slate-300',   textClass: 'text-gray-500' },
  { key: 'unpublished', label: 'Unpublished',  dotClass: 'bg-rose-400',    textClass: 'text-rose-600' },
  { key: 'banned',      label: 'Banned',       dotClass: 'bg-gray-700',    textClass: 'text-gray-700' },
] as const;

type StatusKey = typeof statusLegend[number]['key'];
type TypeCardKey = ListingType;

const overviewSegments = computed<ExpertStatsSegment[]>(() => {
  const { statuses } = stats.value.all_experts;
  const total = stats.value.all_experts.total || 1;
  return statusLegend
    .map(s => ({
      key: s.key,
      label: s.label,
      count: statuses[s.key as StatusKey],
      pct: (statuses[s.key as StatusKey] / total) * 100,
      colorClass: s.dotClass,
      textClass: s.textClass,
    }))
    .filter(s => s.count > 0);
});

const publishRate = computed(() => {
  const total = stats.value.all_experts.total;
  if (!total) return 0;
  return Math.round((stats.value.all_experts.statuses.published / total) * 100);
});

const typeCards = computed(() => [
  {
    key: 'freelancer' as TypeCardKey,
    label: 'Freelancers',
    icon: '◆',
    accent: expertTypeAppearance.freelancer.statsAccentClass,
    bucket: stats.value.types.freelancer,
  },
  {
    key: 'agency' as TypeCardKey,
    label: 'Agencies',
    icon: '■',
    accent: expertTypeAppearance.agency.statsAccentClass,
    bucket: stats.value.types.agency,
  },
  {
    key: 'app_service_provider' as TypeCardKey,
    label: 'App Services',
    icon: '▲',
    accent: expertTypeAppearance.app_service_provider.statsAccentClass,
    bucket: stats.value.types.app_service_provider,
  },
]);

const isOverviewCardActive = computed(() => (
  hoveredOverviewStatus.value !== null ||
  (!!props.activeStatus && !props.activeExpertType)
));

function setHoveredOverviewStatus(status: ExpertProfileStatus | null) {
  hoveredOverviewStatus.value = status;
}

const activeOverviewSegmentKey = computed(() => (
  props.activeExpertType ? undefined : props.activeStatus
));

function applyOverviewFilters(status: ExpertProfileStatus) {
  emit('applyFilters', { status, expertType: '' });
}
</script>

<template>
  <div class="stats-panel" :class="{ 'is-loading': isLoading }">
    <!-- Skeleton -->
    <template v-if="isLoading">
      <div class="stats-skeleton">
        <div class="skel-block h-20 w-48 rounded-lg" />
        <div class="skel-block h-20 flex-1 rounded-lg" />
        <div class="skel-block h-20 flex-1 rounded-lg" />
        <div class="skel-block h-20 flex-1 rounded-lg" />
      </div>
    </template>

    <template v-else>
      <!-- Overview strip -->
      <div class="stats-overview" :class="{ 'is-active': isOverviewCardActive }">
        <div class="overview-hero">
          <div class="hero-number" :class="{ 'is-active': isOverviewCardActive }">{{ stats.all_experts.total }}</div>
          <div class="hero-label">Total Experts</div>
          <div class="hero-rate" v-if="stats.all_experts.total > 0">
            <span class="rate-value">{{ publishRate }}%</span>
            <span class="rate-label">published</span>
          </div>
        </div>

        <!-- Full-width proportional bar -->
        <div class="overview-bar-section">
          <div class="bar-caption">Hover to inspect, click to filter</div>
          <ExpertStatsSegmentedBar
            :segments="overviewSegments"
            :active-key="activeOverviewSegmentKey"
            :aria-label-prefix="'Filter experts by'"
            @hover-change="setHoveredOverviewStatus"
            @select="applyOverviewFilters"
          />
        </div>
      </div>

      <!-- Type breakdown -->
      <div class="type-cards">
        <ExpertStatsTypeCard
          v-for="(tc, i) in typeCards"
          :key="tc.key"
          :label="tc.label"
          :icon="tc.icon"
          :bucket="tc.bucket"
          :accent-class="tc.accent"
          :type-key="tc.key"
          :active-status="activeStatus"
          :active-expert-type="activeExpertType"
          :style="{ animationDelay: `${i * 60 + 100}ms` }"
          class="type-card-enter"
          @apply-filters="emit('applyFilters', $event)"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.stats-panel {
  margin-bottom: 20px;
}

/* ---- skeleton ---- */
.stats-skeleton {
  display: flex;
  gap: 12px;
}

.skel-block {
  background: linear-gradient(
    110deg,
    rgba(0, 0, 0, 0.04) 30%,
    rgba(0, 0, 0, 0.02) 50%,
    rgba(0, 0, 0, 0.04) 70%
  );
  background-size: 200% 100%;
  animation: shimmer 1.6s ease-in-out infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ---- overview ---- */
.stats-overview {
  display: flex;
  align-items: stretch;
  gap: 24px;
  padding: 16px 20px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  margin-bottom: 10px;
  transition: box-shadow 0.22s ease, border-color 0.22s ease, transform 0.22s ease;
}

.stats-overview:hover {
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.stats-overview.is-active {
  border-color: rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.overview-hero {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 110px;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  padding-right: 24px;
}

.hero-number {
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  color: #111;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  transition: color 0.18s ease, transform 0.18s ease;
}

.hero-number.is-active {
  color: #0f172a;
  transform: translateY(-1px);
}

.hero-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-top: 4px;
}

.hero-rate {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 8px;
}

.rate-value {
  font-size: 14px;
  font-weight: 700;
  color: #059669;
  font-variant-numeric: tabular-nums;
}

.rate-label {
  font-size: 11px;
  color: #9ca3af;
}

/* ---- bar ---- */
.overview-bar-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  min-width: 0;
}

.bar-caption {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #9ca3af;
}

@keyframes segGrow {
  from {
    transform: scaleX(0);
    opacity: 0;
  }
  to {
    transform: scaleX(1);
    opacity: 1;
  }
}

/* ---- type cards ---- */
.type-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-card-enter {
  animation: slideUp 0.35s ease-out both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---- responsive ---- */
@media (max-width: 768px) {
  .stats-overview {
    flex-direction: column;
    gap: 14px;
  }

  .overview-hero {
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-right: 0;
    padding-bottom: 14px;
    flex-direction: row;
    align-items: baseline;
    gap: 10px;
  }

  .hero-rate {
    margin-top: 0;
    margin-left: auto;
  }

  .type-cards {
    grid-template-columns: 1fr;
  }
}
</style>
